import React, { useEffect, useState } from "react";
import { Link, Route, useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";

import AuthFrame from "@components/AuthFrame";
import AuthenticationService from "@services/Authentication";

import { applyAccount } from "@store/modules/account";
import {
  setCognitoUser,
  login,
  notifyTimeout,
  notifyDisabledUser,
} from "@store/modules/auth";
import AuthTemplate from "@templates/Auth";
import ServiceInfo from "@src/components/loginComponents/ServiceInfo";
import HireItNow from "@src/components/loginComponents/HireItNow";
import KnowMoreAboutIt from "@src/components/loginComponents/KnowMoreAboutIt";
import ForgotPassword from "@pages/ForgotPassword";
import PasswordCode from "@pages/PasswordCode";
import AccountService from "@services/Account";

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: "100%",
    "@media all and (min-width: 768px)": {
      width: "320px",
    },
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: "#660099",
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(2, 0, 0),
  },
  forgotPassword: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  forgotPasswordLink: {
    color: "#3f51b5!important",
    cursor: "pointer",
    textDecoration: "none!important",
  },
}));

function Login(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  let emailInputRef = null;

  function navigateToNewPassword() {
    props.history.push("/cadastro-senha");
  }

  function handleLogged(user, account) {
    dispatch(applyAccount(account));
    dispatch(login(user));
    setLoading(false);
  }

  function executeLogin(values) {
    if (loading) {
      return;
    }

    setLoading(true);
    AuthenticationService.login(values.login, values.password)
      .then((user) => {
        AccountService.getAccountDetail()
          .then((account) => {
            handleLogged(user, account);
          })
          .catch((e) => {
            AuthenticationService.logout()
              .catch(() => {
                return true;
              })
              .then(() => {
                console.error(e);
                toast.error("Erro ao carregar dados da empresa!");
                setLoading(false);
              });
          });
      })
      .catch((error) => {
        if (error && error.code === "NEW_PASSWORD_REQUIRED") {
          dispatch(setCognitoUser(error.data));
          navigateToNewPassword();
        } else {
          toast.warn("Acesso inválido!");
        }
        setLoading(false);
      });
  }

  const timeoutNotification = useSelector(
    (state) => state.auth.timeoutNotification
  );
  const disabledUserNotification = useSelector(
    (state) => state.auth.disabledUserNotification
  );

  useEffect(() => {
    function showSessionExpiredAlert() {
      toast.warn("Tempo de sessão expirado!");
      dispatch(notifyTimeout(false));
    }

    function showDisabledUserAlert() {
      toast.warn("Acesso negado!");
      dispatch(notifyDisabledUser(false));
    }

    if (timeoutNotification) {
      showSessionExpiredAlert();
    }

    if (disabledUserNotification) {
      showDisabledUserAlert();
    }
  }, [timeoutNotification, disabledUserNotification, dispatch]);

  function handleServiceInfoCancel() {
    history.push("/login");
  }

  function handleServiceInfoSuccess() {
    history.push("/login/contato");
  }

  const initialValues = {
    login: "",
    password: "",
  };
  const validationSchema = Yup.object().shape({
    login: Yup.string().required("Campo obrigatório"),
    password: Yup.string().required("Campo obrigatório"),
  });

  return (
    <AuthTemplate>
      <AuthFrame banner="left">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          isInitialValid={false}
        >
          {({ errors, touched, isValid, values, handleChange, handleBlur }) => (
            <div className={classes.paper}>
              <Typography component="h1" variant="h5">
                Login
              </Typography>
              {/* login input */}
              <TextField
                value={values.login}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.login ? errors.login : ""}
                error={touched.login && Boolean(errors.login)}
                variant="outlined"
                margin="normal"
                fullWidth
                required
                id="login"
                label="Usuário"
                name="login"
                autoComplete="login"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && emailInputRef) {
                    emailInputRef.focus();
                  }
                }}
              />
              {/* end login input */}
              {/* email input */}
              <TextField
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.password ? errors.password : ""}
                error={touched.password && Boolean(errors.password)}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="Senha"
                type="password"
                id="password"
                autoComplete="password"
                inputRef={(element) => {
                  emailInputRef = element;
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && isValid && !loading) {
                    executeLogin(values);
                  }
                }}
              />
              {/* end email input */}
              <div className={classes.forgotPassword}>
                <Link
                  to="/esqueceu-a-senha"
                  className={classes.forgotPasswordLink}
                >
                  <Typography>Esqueci minha senha</Typography>
                </Link>
              </div>
              {/* submit button */}
              <Button
                onClick={() => {
                  executeLogin(values);
                }}
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                className={classes.submit}
                disabled={!isValid || loading}
              >
                Entrar
              </Button>
              {/* end submit button */}

              {/* extra info button */}
              <Button
                onClick={() => history.push("/login/contratar")}
                fullWidth
                variant="contained"
                color=""
                size="large"
                className={classes.submit}
                // disabled={!isValid || loading}
              >
                Ainda não adquiriu este serviço?
              </Button>
              {/* end extra info button */}
            </div>
          )}
        </Formik>
      </AuthFrame>

      <Route path="/login/contratar">
        <ServiceInfo
          open={true}
          onCancel={handleServiceInfoCancel}
          onAgree={handleServiceInfoSuccess}
        />
      </Route>

      <Route path="/login/contato">
        <HireItNow open={true} onCancel={handleServiceInfoCancel} />
      </Route>

      <Route path="/login/saiba-mais">
        <KnowMoreAboutIt open={true} onCancel={handleServiceInfoCancel} />
      </Route>

      <Route path="/esqueceu-a-senha">
        <ForgotPassword />
      </Route>

      <Route path="/codigo-senha">
        <PasswordCode />
      </Route>
    </AuthTemplate>
  );
}

export default Login;
